/**
 * Trip-date bounds for `<input type="datetime-local">` limits and Zod refinements.
 *
 * The trip's startDate is taken as day-start, endDate as day-end (23:59), so
 * activities can be added at any moment within the trip's days — you don't get
 * blocked at 00:00 on the last day.
 */

import type { Timestamp, Trip } from "@travelix/core";

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Parse a `yyyy-MM-dd` string (from `<input type="date">`) as LOCAL midnight.
 *
 * `new Date("2027-09-16")` is spec-defined as UTC midnight; `Timestamp.fromDate`
 * then stores that instant, which reads back as the previous day in any
 * negative-UTC-offset locale (America/Costa_Rica, America/New_York, etc.).
 * Callers should route every `type="date"` value through this helper so the
 * date the user picked is the date we persist, no matter their timezone.
 *
 * Returns `null` for inputs that don't match `yyyy-MM-dd`.
 */
export function parseYmdLocal(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Format any Timestamp/Date as `yyyy-MM-ddTHH:mm` (local, no seconds). */
export function toDateTimeLocal(
  input: Timestamp | Date | null | undefined,
): string {
  if (!input) return "";
  const d = "toDate" in input ? input.toDate() : input;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface TripDateBounds {
  /** Local `datetime-local` string for the trip's start (00:00). */
  minAttr: string;
  /** Local `datetime-local` string for the trip's end (23:59). */
  maxAttr: string;
  /** JS Date object at start of the trip (used for Zod bounds checks). */
  minMs: number;
  /** JS Date object at end of the trip (used for Zod bounds checks). */
  maxMs: number;
  /** Trip's start day for pretty display: "Jun 3". */
  startLabel: string;
  /** Trip's end day for pretty display: "Jun 10". */
  endLabel: string;
}

/** Derive the date-bound envelope from a trip. */
export function getTripDateBounds(trip: Trip): TripDateBounds {
  const start = trip.startDate.toDate();
  const startOfDay = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0);

  const end = trip.endDate.toDate();
  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59);

  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return {
    minAttr: toDateTimeLocal(startOfDay),
    maxAttr: toDateTimeLocal(endOfDay),
    minMs: startOfDay.getTime(),
    maxMs: endOfDay.getTime(),
    startLabel: fmt(startOfDay),
    endLabel: fmt(endOfDay),
  };
}

/** True when the given `datetime-local` value falls within the trip window. */
export function isWithinBounds(
  value: string | undefined | null,
  bounds: TripDateBounds,
): boolean {
  if (!value) return true; // empty is allowed — enforce required-ness elsewhere
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return false;
  return t >= bounds.minMs && t <= bounds.maxMs;
}
